import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Radio,
    Typography,
} from '@mui/material';
import Modal from '@mui/material/Modal';
import type { BookingPrecheckResponse } from 'api/models/BookingPrecheck.type';
import { displayPriceFunction } from 'components/Common/DisplayValues/DisplayPrice';
import { formatCurrency, type PolicyMessage } from 'hooks/useTravelPolicy';
import React from 'react';
import IconCar from '../../../../../assets/icon/icon-car.svg';
import IconChaffuer from '../../../../../assets/icon/icon-chaffeur.svg';
import IconGreenBadge from '../../../../../assets/icon/icon-green-badge.svg';
import IconInfo from '../../../../../assets/icon/icon-info.svg';
import IconTime from '../../../../../assets/icon/icon-time.svg';
import type {
    FlexibleOption,
    HandleBookCar,
    QuotesProp,
} from '../QuotesTitle';
import ModalDetails from './ModalDetails';

type ModalDataQuote = {
  title: string
  example: string
  cardist: string
  image: string
  seats: number
  luggage: number
  quotes?: Partial<QuotesProp>
}

type TravelPolicyConfig = {
  locale?: string
  precheck: (estimatedPrice: number) => Promise<BookingPrecheckResponse>
  buildMessage: (
    decision: BookingPrecheckResponse,
    opts?: { locale?: string; showCompliantMessage?: boolean },
  ) => PolicyMessage | null
  buildDialogText: (
    decision: BookingPrecheckResponse,
    opts?: { locale?: string; showCompliantMessage?: boolean },
  ) => string
}

interface ModalProp {
  data: ModalDataQuote
  openModal: boolean
  isImmediateJourney: boolean
  displayVATPrice: boolean
  selectedVehicles: any
  setSelectedVehicles: any
  isFlexible: FlexibleOption | null
  numOfVehicle: number
  isOneWay: boolean
  client?: any
  isAsDirected: boolean
  setNumOfVehicle: React.Dispatch<React.SetStateAction<number>>
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  handleBookCar: (value: HandleBookCar) => void
  travelPolicy?: TravelPolicyConfig
}

const untrackedMessage = `The provider you are booking does not offer real time tracking, meaning its location won't be visible on the map. You'll still receive live updates to keep you informed throughout your journey.`

const VehicleDetailsModal = ({
  data,
  openModal,
  isOneWay,
  isAsDirected,
  isImmediateJourney,
  displayVATPrice,
  selectedVehicles,
  isFlexible,
  setSelectedVehicles,
  client,
  setOpenModal,
  handleBookCar,
  numOfVehicle,
  setNumOfVehicle,
  travelPolicy,
}: ModalProp) => {
  const isMountedRef = React.useRef(true)
  React.useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const { applicationTheme } = client.client

  const [isFlexibleOption, setFlexibleOption] = React.useState(false)

  const [policyDialogOpen, setPolicyDialogOpen] = React.useState(false)
  const [policyChecking, setPolicyChecking] = React.useState(false)
  const [policyDecision, setPolicyDecision] =
    React.useState<BookingPrecheckResponse | null>(null)
  const [policyMessage, setPolicyMessage] =
    React.useState<PolicyMessage | null>(null)
  const [policyDialogText, setPolicyDialogText] = React.useState<string>('')
  const [policyCheckError, setPolicyCheckError] = React.useState<string | null>(
    null,
  )

  const getActiveQuote = () => (isFlexibleOption ? isFlexible : data.quotes)

  const getSelectedAmountBreakdown = () => {
    const activeQuote: any = getActiveQuote()
    const price: any = activeQuote?.price
    const currency =
      price?.currency || activeQuote?.estimatedPriceCurrency || 'GBP'
    const locale = travelPolicy?.locale ?? 'en-GB'

    const exVat =
      typeof price?.price === 'number' && Number.isFinite(price.price)
        ? price.price
        : null
    const vat =
      typeof price?.vat === 'number' && Number.isFinite(price.vat)
        ? price.vat
        : null
    const incVat =
      typeof price?.total === 'number' && Number.isFinite(price.total)
        ? price.total
        : null

    return { currency: String(currency), locale, exVat, vat, incVat }
  }

  const getCurrencyCode = () => {
    const activeQuote: any = getActiveQuote()
    const currency =
      activeQuote?.estimatedPriceCurrency ||
      activeQuote?.price?.currency ||
      'GBP'
    return String(currency)
  }

  const getDistanceOverMiles = (decision: BookingPrecheckResponse) => {
    const code = String((decision as any)?.breach?.reason?.code ?? '')
      .trim()
      .toUpperCase()
    if (!code.includes('DISTANCE')) return null

    const amountOver = (decision as any)?.breach?.amountOver
    const miles =
      typeof amountOver === 'number'
        ? amountOver
        : typeof amountOver?.total === 'number'
        ? amountOver.total
        : null

    return typeof miles === 'number' && Number.isFinite(miles) ? miles : null
  }

  const getAmountOverDisplay = (decision: BookingPrecheckResponse) => {
    const breach: any = (decision as any)?.breach
    if (!breach?.amountOver) return null

    const reasonCode = String(breach?.reason?.code ?? '')
      .trim()
      .toUpperCase()
    if (reasonCode.includes('DISTANCE')) return null

    const amountOver = breach.amountOver
    const amount =
      typeof amountOver === 'number'
        ? amountOver
        : typeof amountOver?.total === 'number'
        ? amountOver.total
        : null

    if (typeof amount !== 'number' || !Number.isFinite(amount)) return null

    const currency =
      (typeof amountOver === 'object' && amountOver
        ? (amountOver as any).currency
        : undefined) || getCurrencyCode()
    const locale = travelPolicy?.locale ?? 'en-GB'

    return formatCurrency(amount, String(currency), locale)
  }

  const distanceOverMiles = policyDecision
    ? getDistanceOverMiles(policyDecision)
    : null

  const amountOverDisplay = policyDecision
    ? getAmountOverDisplay(policyDecision)
    : null

  const resetPolicyState = () => {
    setPolicyDialogOpen(false)
    setPolicyDecision(null)
    setPolicyMessage(null)
    setPolicyDialogText('')
    setPolicyCheckError(null)
    setPolicyChecking(false)
  }

  const extractEstimatedPrice = (quote: any): number | null => {
    console.log('vehicle', quote)
    const raw =
      quote?.price?.total ?? quote?.price?.price ?? quote?.price ?? null
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
  }

  const isNoPolicyResponse = (decision: BookingPrecheckResponse | null) => {
    return (
      String((decision as any)?.status ?? '')
        .trim()
        .toUpperCase() === 'NO_POLICY'
    )
  }

  const isNoPolicyError = (err: any) => {
    const code = String(
      err?.response?.data?.error?.code ??
        err?.response?.data?.code ??
        err?.code ??
        '',
    )
      .trim()
      .toUpperCase()

    if (code === 'NO_POLICY') return true

    const message = String(
      err?.response?.data?.error?.message ?? err?.message ?? '',
    )
      .trim()
      .toUpperCase()

    return message.includes('NO_POLICY')
  }

  const completeBookingSelection = () => {
    const vehicleQuote = isFlexibleOption ? isFlexible : data.quotes
    const newCount = numOfVehicle + 1

    setNumOfVehicle(newCount)
    setSelectedVehicles({
      ...selectedVehicles,
      [vehicleQuote?.supplierQuoteReference || '']: {
        supplierQuoteReference: vehicleQuote?.supplierQuoteReference,
        inboundSupplierQuoteReference:
          vehicleQuote?.inboundSupplierQuoteReference,
        quantity: newCount,
        price: parseFloat((vehicleQuote?.price?.price || 0).toFixed(2)),
        priceCurrency: vehicleQuote?.estimatedPriceCurrency,
        seats: data?.seats,
        title: data?.title,
      },
    })

    handleBookCar({
      data: {
        supplierQuoteReference: vehicleQuote?.supplierQuoteReference || '',
        seats: data.seats,
        estimatedPriceCurrency: vehicleQuote?.estimatedPriceCurrency || '',
        price: vehicleQuote?.price || 0,
        inboundSupplierQuoteReference:
          vehicleQuote?.inboundSupplierQuoteReference || '',
        quantity: newCount,
      },
      vehicleDetails: { ...data, quotes: vehicleQuote },
    })

    const wrapper = document.getElementById('jyrney-booking-wrapper')
    if (wrapper) {
      wrapper.scrollTo(0, 0)
    }

    setOpenModal(false)
  }

  const handleBookClick = async () => {
    if (!travelPolicy) {
      completeBookingSelection()
      return
    }

    if (policyChecking) return

    const vehicleQuote = isFlexibleOption ? isFlexible : data.quotes
    const estimatedPrice = extractEstimatedPrice(vehicleQuote)

    if (estimatedPrice == null) {
      setPolicyCheckError('Unable to check travel policy: price is missing.')
      setPolicyDialogOpen(true)
      return
    }

    const locale = travelPolicy.locale ?? 'en-GB'

    setPolicyChecking(true)
    setPolicyCheckError(null)

    try {
      console.log('vehicle', estimatedPrice)
      const decision = await travelPolicy.precheck(estimatedPrice)
      if (isNoPolicyResponse(decision)) {
        completeBookingSelection()
        return
      }

      const msg = travelPolicy.buildMessage(decision, { locale })

      if (
        (decision as any)?.status === 'COMPLIANT' ||
        (decision as any)?.status === 'NO_POLICY'
      ) {
        completeBookingSelection()
        return
      }

      const isAllowed =
        Boolean((decision as any)?.allowed) ||
        Boolean((decision as any)?.overridable)

      if (isAllowed && !msg) {
        completeBookingSelection()
        return
      }

      if (!isMountedRef.current) return

      setPolicyDecision(decision)
      setPolicyMessage(msg)
      setPolicyDialogText(
        msg ? '' : travelPolicy.buildDialogText(decision, { locale }),
      )
      setPolicyDialogOpen(true)
    } catch (err: any) {
      if (isNoPolicyError(err)) {
        completeBookingSelection()
        return
      }

      const message =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Unable to process booking precheck. Please try again.'
      if (!isMountedRef.current) return
      setPolicyCheckError(message)
      setPolicyDialogOpen(true)
    } finally {
      if (isMountedRef.current) {
        setPolicyChecking(false)
      }
    }
  }

  const btnPrice = () => {
    const additionalOptionPrice = Math.abs(
      Number(
        displayPriceFunction(isFlexible?.price, displayVATPrice)?.exactPrice ||
          0,
      ) ||
        0 -
          (Number(
            displayPriceFunction(data.quotes?.price, displayVATPrice)
              ?.exactPrice || 0,
          ) || 0),
    )
    const regularPrice =
      Number(
        displayPriceFunction(data.quotes?.price, displayVATPrice)?.exactPrice ||
          0,
      ) || 0

    const currency = displayPriceFunction(
      data.quotes?.price,
      displayVATPrice,
    )?.currencySymbolValue

    return {
      price: isFlexibleOption ? additionalOptionPrice : regularPrice,
      currency,
    }
  }

  const renderPolicySelectedAmounts = () => {
    if (!policyDecision) return null

    const breakdown = getSelectedAmountBreakdown()

    if (displayVATPrice) {
      const value = breakdown.incVat ?? btnPrice().price
      return (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Selected amount (inc. VAT):{' '}
          {Number.isFinite(value)
            ? formatCurrency(value, breakdown.currency, breakdown.locale)
            : '-'}
        </Typography>
      )
    }

    return (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Selected amount (ex. VAT):{' '}
          {breakdown.exVat == null
            ? '-'
            : formatCurrency(
                breakdown.exVat,
                breakdown.currency,
                breakdown.locale,
              )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          VAT:{' '}
          {breakdown.vat == null
            ? '-'
            : formatCurrency(
                breakdown.vat,
                breakdown.currency,
                breakdown.locale,
              )}
        </Typography>
      </>
    )
  }

  return (
    <Modal
      disableScrollLock
      open={openModal}
      onClose={() => {
        setOpenModal(false)
        setSelectedVehicles({})
        setNumOfVehicle(0)
        resetPolicyState()
      }}
    >
      <>
        <Dialog
          open={policyDialogOpen}
          onClose={() => {
            setPolicyDialogOpen(false)
            setPolicyDecision(null)
            setPolicyMessage(null)
            setPolicyDialogText('')
            setPolicyCheckError(null)
          }}
          aria-labelledby="booking-policy-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="booking-policy-dialog-title">
            Booking Policy Check
          </DialogTitle>
          <DialogContent>
            {policyDecision && policyMessage?.policyName ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Policy: {policyMessage.policyName}
              </Typography>
            ) : null}

            {typeof distanceOverMiles === 'number' ? (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                Distance over policy: {distanceOverMiles} mile
                {distanceOverMiles === 1 ? '' : 's'}
              </Typography>
            ) : null}

            {renderPolicySelectedAmounts()}

            {amountOverDisplay ? (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Amount over policy (inc. VAT): {amountOverDisplay}
              </Typography>
            ) : null}

            {policyCheckError ? (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {policyCheckError}
              </Typography>
            ) : policyMessage ? (
              <Box>
                <Typography>{policyMessage.title}</Typography>
                {policyMessage.body ? (
                  <Typography sx={{ mt: 1 }}>{policyMessage.body}</Typography>
                ) : null}
              </Box>
            ) : null}

            {policyDialogText ? (
              <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
                {policyDialogText}
              </DialogContentText>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPolicyDialogOpen(false)
                setPolicyDecision(null)
                setPolicyMessage(null)
                setPolicyDialogText('')
                setPolicyCheckError(null)
              }}
              variant="contained"
            >
              Go back
            </Button>
            {policyDecision &&
            (Boolean((policyDecision as any)?.allowed) ||
              Boolean((policyDecision as any)?.overridable)) ? (
              <Button
                variant="contained"
                onClick={() => {
                  setPolicyDialogOpen(false)
                  setPolicyDecision(null)
                  setPolicyMessage(null)
                  setPolicyDialogText('')
                  setPolicyCheckError(null)
                  completeBookingSelection()
                }}
              >
                Continue
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>

        <Box
          component="div"
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '40%',
            minWidth: '1172px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            display: 'flex',
            maxHeight: '780px',
            overflow: 'auto',
            borderRadius: '5px',
            '@media (max-height: 784px)': {
              height: '90vh',
            },
          }}
          className="custom-scrollbar"
        >
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '548px',
              padding: '24px',
              height: '100%',
            }}
          >
            <Box
              component="div"
              sx={{
                width: '500px',
                height: '400px',
                backgroundColor: '#F7F7F7',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '24px',
                position: 'relative',
                borderRadius: '5.13px',
              }}
            >
              {data?.image && (
                <Box
                  component="img"
                  src={data?.image}
                  style={{
                    height: 'auto',
                    width: '100%',
                  }}
                />
              )}
              <Box
                component="div"
                sx={{
                  position: 'absolute',
                  top: '4px',
                  left: '4px',
                  display: 'flex',
                }}
              >
                {data.title?.toLowerCase()?.includes('chauffer') && (
                  <Box
                    component="img"
                    src={IconChaffuer}
                    sx={{
                      width: '96px',
                      height: '96px',
                    }}
                  />
                )}
                {(data.title?.toLowerCase()?.includes('zero') ||
                  data.title?.toLowerCase()?.includes('hybrid')) && (
                  <Box
                    component="img"
                    src={IconGreenBadge}
                    sx={{
                      width: '96px',
                      height: '96px',
                    }}
                  />
                )}
              </Box>
            </Box>
            {!data.quotes?.isTrackable && (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  marginTop: '24px',
                }}
              >
                <Typography
                  component="p"
                  sx={() => ({
                    color: '#414042',
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '21px',
                  })}
                >
                  This car is
                  <Typography
                    component="span"
                    sx={() => ({
                      color: '#414042',
                      fontWeight: '700',
                      fontSize: '14px',
                      lineHeight: '21px',
                      marginLeft: '4px',
                    })}
                  >
                    untracked.
                  </Typography>
                </Typography>
                <Box
                  component="div"
                  className="tooltip-wrapper"
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    component="img"
                    src={IconInfo}
                    alt="info"
                    sx={{
                      marginLeft: '4px',
                    }}
                  />
                  <Box
                    component="div"
                    className="tooltip left-untracked"
                    sx={{
                      width: '281px',
                      zIndex: 20,
                      left: '-128px',
                      top: 'unset',
                      bottom: '26px',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={() => ({
                        color: '#fff',
                        fontWeight: '400',
                        fontSize: '12px',
                        lineHeight: '16px',
                      })}
                    >
                      {untrackedMessage}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {data?.quotes?.isPreferredSupplier ? (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: '32px',
                  visibility: data?.quotes?.isPreferredSupplier
                    ? 'visible'
                    : 'hidden',
                }}
              >
                <Typography
                  component="p"
                  sx={() => ({
                    color: '#414042',
                    fontWeight: '300',
                    fontSize: '16px',
                    lineHeight: '21px',
                  })}
                >
                  Your ride will be coming from:
                </Typography>
                {data.quotes?.supplierLogo ? (
                  <Box
                    component="img"
                    src={data.quotes?.supplierLogo}
                    alt="chevron"
                    sx={{
                      width: '140px',
                      height: 'auto',
                      marginTop: '12px',
                    }}
                  />
                ) : (
                  <Typography
                    component="p"
                    sx={(theme) => ({
                      color: theme.palette.brand.dark,
                      fontWeight: '700',
                      fontSize: '20px',
                      lineHeight: '20px',
                      textTransform: 'uppercase',
                      marginTop: '12px',
                    })}
                  >
                    {data.quotes?.supplierName}
                  </Typography>
                )}
              </Box>
            ) : (
              <></>
            )}
          </Box>
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '624px',
              padding: '32px 16px 32px 24px',
              justifyContent: 'space-between',
              position: 'relative',
              maxHeight: '735px',
              overflow: 'auto',
            }}
            className="custom-scrollbar"
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '75px',
              }}
            >
              <Box
                component="div"
                sx={() => ({
                  display: 'flex',
                  justifyContent: 'space-between',
                })}
              >
                <Typography
                  component="p"
                  sx={() => ({
                    color: '#0A2435',
                    fontWeight: '600',
                    fontSize: '19px',
                    lineHeight: '21px',
                  })}
                >
                  {data?.title}
                </Typography>
                <Typography
                  component="p"
                  sx={() => ({
                    color: '#414042',
                    fontWeight: '600',
                    fontSize: '19px',
                    lineHeight: '21px',
                  })}
                >
                  {`${btnPrice().currency} ${btnPrice().price.toFixed(2)}`}
                </Typography>
              </Box>
              <Typography
                component="p"
                sx={() => ({
                  color: '#414042',
                  fontWeight: '300',
                  fontSize: '17px',
                  lineHeight: '21px',
                  marginTop: '8px',
                })}
              >
                {data?.cardist}
              </Typography>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '16px',
                  marginTop: '16px',
                }}
              >
                {data.cardist && (
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      fontWeight: 'normal',
                      alignItems: 'center',
                      gap: '5px',
                      alignSelf: 'flex-end',
                    }}
                  >
                    <Box
                      component="img"
                      src={IconCar}
                      alt="info"
                      sx={{
                        marginLeft: '4px',
                        width: '24px',
                        height: '24px',
                      }}
                    />
                    <Typography
                      component="span"
                      sx={() => ({
                        color: '#414042',
                        fontWeight: '400',
                        fontSize: '16px',
                        lineHeight: '19.36px',
                      })}
                    >
                      {data?.example}
                    </Typography>
                  </Box>
                )}
                {data.seats && (
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      fontWeight: 'normal',
                      alignItems: 'center',
                      gap: '5px',
                      alignSelf: 'flex-end',
                    }}
                  >
                    <PeopleOutlineIcon />
                    <span>{data.seats}</span>
                  </Box>
                )}
                {data.luggage && (
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      fontWeight: 'normal',
                      alignItems: 'center',
                      gap: '5px',
                      alignSelf: 'flex-end',
                    }}
                  >
                    <WorkOutlineIcon />
                    <span>{data.luggage}</span>
                  </Box>
                )}
              </Box>
              {isFlexible?.vehicleTypeName && (
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginTop: '24px',
                      justifyContent: 'space-between',
                      marginLeft: '-12px',
                    }}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        width: '100%',
                      }}
                    >
                      <Box
                        component="div"
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          component="div"
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <Radio
                            checked={!isFlexibleOption}
                            onChange={() => setFlexibleOption(false)}
                            value="a"
                            name="radio-buttons"
                          />
                          <Typography
                            component="p"
                            onClick={() => setFlexibleOption(false)}
                            sx={() => ({
                              color: '#414042',
                              fontWeight: '300',
                              fontSize: '16px',
                              lineHeight: '21px',
                              cursor: 'pointer',
                            })}
                          >
                            Non-refundable with less than 12-hour notice
                          </Typography>
                        </Box>
                        <Typography
                          component="p"
                          sx={() => ({
                            color: '#414042',
                            fontWeight: '400',
                            fontSize: '16px',
                            lineHeight: '24px',
                          })}
                        >
                          {`+ ${btnPrice().currency}0.00`}
                        </Typography>
                      </Box>
                      <Box
                        component="div"
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          component="div"
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: 'calc(100% - 100px)',
                          }}
                        >
                          <Radio
                            checked={isFlexibleOption}
                            onChange={() => setFlexibleOption(true)}
                            value="b"
                            name="radio-buttons"
                          />
                          <Typography
                            component="p"
                            onClick={() => setFlexibleOption(true)}
                            sx={() => ({
                              color: '#414042',
                              fontWeight: '300',
                              fontSize: '16px',
                              lineHeight: '21px',
                              cursor: 'pointer',
                            })}
                          >
                            {`Fully refundable until
                              ${
                                isFlexible?.vehicleDetails
                                  ?.fullyRefundableUntil || '-'
                              }
                            `}
                          </Typography>
                        </Box>
                        <Typography
                          component="p"
                          sx={() => ({
                            color: '#414042',
                            fontWeight: '400',
                            fontSize: '16px',
                            lineHeight: '24px',
                          })}
                        >
                          {`+ ${
                            displayPriceFunction(
                              data.quotes?.price,
                              displayVATPrice,
                            )?.currencySymbolValue
                          }${Math.abs(
                            (Number(
                              displayPriceFunction(
                                data.quotes?.price,
                                displayVATPrice,
                              )?.exactPrice || 0,
                            ) || 0) -
                              (Number(
                                displayPriceFunction(
                                  isFlexible?.price,
                                  displayVATPrice,
                                )?.exactPrice || 0,
                              ) || 0),
                          ).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: '40px',
                  gap: '8px',
                }}
              >
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                  }}
                >
                  <Typography
                    component="p"
                    sx={() => ({
                      color: '#414042',
                      fontWeight: '600',
                      fontSize: '17px',
                      lineHeight: '21px',
                    })}
                  >
                    Cancellation Policy
                  </Typography>

                  <Box
                    component="div"
                    className="tooltip-wrapper"
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                    }}
                  >
                    <Box component="img" src={IconInfo} alt="info" />
                    <Box
                      component="div"
                      className="tooltip untracked"
                      sx={{
                        width: '281px',
                        zIndex: 20,
                        left: '-133px',
                        top: '27px',
                      }}
                    >
                      <Typography
                        component="span"
                        sx={() => ({
                          color: '#fff',
                          fontWeight: '400',
                          fontSize: '12px',
                          lineHeight: '16px',
                        })}
                      >
                        {isFlexibleOption
                          ? isFlexible?.vehicleDetails?.cancellationPolicy
                          : data?.quotes?.vehicleDetails?.cancellationPolicy ||
                            '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <ModalDetails
                price={
                  Number(
                    displayPriceFunction(data.quotes?.price, displayVATPrice)
                      ?.exactPrice || 0,
                  ) || 0
                }
                currency={btnPrice().currency || ''}
                isFlexibleOption={isFlexibleOption}
                displayVat={displayVATPrice}
                additionalOptions={
                  isFlexibleOption
                    ? Math.abs(
                        (Number(
                          displayPriceFunction(
                            isFlexible?.price,
                            displayVATPrice,
                          )?.exactPrice || 0,
                        ) || 0) -
                          (Number(
                            displayPriceFunction(
                              data.quotes?.price,
                              displayVATPrice,
                            )?.exactPrice || 0,
                          ) || 0),
                      )
                    : isFlexible?.vehicleTypeName
                    ? 'displayOnly'
                    : 0
                }
              />
              {applicationTheme.name === 'Jyrney' && (
                <Box sx={{ paddingTop: '20px' }}>
                  <Typography
                    sx={{
                      color: '#414042',
                      fontWeight: '400',
                      fontSize: '17px',
                      lineHeight: '21px',
                      textAlign: 'end',
                    }}
                  >
                    By clicking{' '}
                    <span style={{ fontWeight: 600 }}>
                      {isOneWay || isAsDirected ? 'Book' : 'Add to Booking'}{' '}
                    </span>
                    you agree to{' '}
                    <a
                      href="https://jyrney.com/terms-and-conditions"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'blue' }}
                    >
                      Jyrney&apos;s Terms & Conditions
                    </a>
                  </Typography>
                </Box>
              )}
            </Box>
            <Box>hhh</Box>
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'fixed',
                width: 'calc(100% - 587px)',
                bottom: '0',
                paddingBottom: '32px',
                background: '#fff',
              }}
            >
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  borderRadius: '4px',
                  padding: '12px',
                  gap: '6px',
                  backgroundColor: '#EBF4FF',
                  height: 'max-content',
                  alignItems: 'center',
                  visibility: isImmediateJourney ? 'visible' : 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={IconTime}
                  alt="info"
                  sx={{
                    marginLeft: '4px',
                    width: '24px',
                    height: '24px',
                  }}
                />
                <Typography
                  component="p"
                  sx={() => ({
                    color: '#002EA5',
                    fontWeight: '400',
                    fontSize: '20px',
                    lineHeight: '24px',
                    height: 'max-content',
                  })}
                >
                  {`ETA ${
                    data.quotes?.eta == 99
                      ? 'unavailable'
                      : `${data.quotes?.eta} mins`
                  }`}
                </Typography>
              </Box>

              <Button
                sx={{ fontSize: '16px', padding: '16px' }}
                onClick={handleBookClick}
                disabled={policyChecking}
              >
                {policyChecking ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    CHECKING...
                  </>
                ) : isOneWay || isAsDirected ? (
                  `BOOK FOR ${btnPrice().currency} ${btnPrice().price.toFixed(
                    2,
                  )}`
                ) : (
                  'Add to Booking'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </>
    </Modal>
  )
}

export default VehicleDetailsModal
